import React, { useState, useEffect } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { allLinks, getValByLang } from "../../../../utils/helpers";
import { memo } from "react";
import { setCategorySelected } from "../../../../services/store/storeSlice";
import { useDispatch } from "react-redux";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { Link } from "react-router-dom";
import "./SliderCategoryNew.css";

function SliderCategory({ categories, language }) {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(0);

  // Calculate items per page based on screen size
  const getItemsPerPage = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 480) return 2;
      if (window.innerWidth < 768) return 2;
      if (window.innerWidth < 1024) return 3;
      if (window.innerWidth < 1400) return 4;
      return 5;
    }
    return 5;
  };

  const [itemsPerPage, setItemsPerPage] = useState(getItemsPerPage());

  // Update items per page on window resize
  useEffect(() => {
    const handleResize = () => {
      const newItemsPerPage = getItemsPerPage();
      setItemsPerPage(newItemsPerPage);
      // Reset to first page if current page would be invalid
      const maxPage = Math.ceil(categories.length / newItemsPerPage) - 1;
      if (currentPage > maxPage) {
        setCurrentPage(0);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentPage, categories.length]);

  const totalPages = Math.ceil(categories.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const visibleCategories = categories.slice(startIndex, startIndex + itemsPerPage);

  const goToNext = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const goToPrev = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const goToPage = (pageIndex) => {
    setCurrentPage(pageIndex);
  };

  // Auto-play functionality
  useEffect(() => {
    if (totalPages > 1) {
      const interval = setInterval(() => {
        goToNext();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [currentPage, totalPages]);

  const handleCategoryClick = (category) => {
    dispatch(setCategorySelected([category.id, ...category.all_child]));
  };

  return (
    <Box className="category-widget-container">
      {/* Header with Navigation */}
      <Box className="category-header">
        <Typography variant="h6" fontWeight="bold" className="category-section-title">
          Categories
        </Typography>
        
        {totalPages > 1 && (
          <Box className="category-navigation">
            <IconButton 
              onClick={goToPrev} 
              className="category-nav-button category-prev-button"
              disabled={totalPages <= 1}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton 
              onClick={goToNext} 
              className="category-nav-button category-next-button"
              disabled={totalPages <= 1}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Categories Grid */}
      <Box className="categories-grid">
        {visibleCategories.map((category) => (
          <Box
            key={category.id}
            component={Link}
            to={`${allLinks[18]}/${category.slug}`}
            onClick={() => handleCategoryClick(category)}
            className="category-card"
          >
            <Box className="category-image-container">
              <LazyLoadImage
                src={category.image_url}
                alt={getValByLang(category.name, language)}
                className="category-image"
                effect="blur"
                placeholderSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100%25' height='100%25' fill='%23f0f0f0'/%3E%3C/svg%3E"
              />
            </Box>
            <Typography className="category-name">
              {getValByLang(category.name, language)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default memo(SliderCategory);
